import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Input, Select } from "../components/UI";
import { toast } from "sonner";
import {
  RefreshCcw, CheckCircle2, XCircle, Loader2, RotateCcw,
  Radar, ListChecks, ScrollText, Crosshair,
} from "lucide-react";

const Spinner = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center" data-testid="sa-loading">
    <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />
    <span>{label}</span>
    <span className="text-xs text-slate-600">(live platform call — can take 5–10s)</span>
  </div>
);

const TABS = [
  { key: "overview", label: "Connection", icon: Radar },
  { key: "campaigns", label: "Live SA Campaigns", icon: ListChecks },
  { key: "synclog", label: "Sync Log", icon: ScrollText },
  { key: "pixels", label: "Conversion Pixels", icon: Crosshair },
];

export default function StackAdaptAdmin() {
  const [tab, setTab] = useState("overview");
  const [health, setHealth] = useState(null);
  const [advs, setAdvs] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [saCampaigns, setSaCampaigns] = useState(null);
  const [log, setLog] = useState(null);
  const [pixels, setPixels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pixelForm, setPixelForm] = useState({ name: "", advertiser_id: "" });
  const [creatingPixel, setCreatingPixel] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [h, a, m] = await Promise.all([
        api.get("/stackadapt/health"),
        api.get("/stackadapt/advertisers"),
        api.get("/stackadapt/sync/mappings"),
      ]);
      setHealth(h.data); setAdvs(a.data.advertisers || []); setMappings(m.data || []);
    } catch (e) {
      setHealth({ connected: false, reason: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (tab === "campaigns" && saCampaigns === null) {
      api.get("/stackadapt/sa-campaigns").then(r => setSaCampaigns(r.data)).catch(() => setSaCampaigns({ nodes: [], total: 0 }));
    }
    if (tab === "synclog" && log === null) {
      api.get("/stackadapt/sync/log").then(r => setLog(r.data)).catch(() => setLog([]));
    }
    if (tab === "pixels" && pixels === null) {
      api.get("/stackadapt/pixels").then(r => setPixels(r.data)).catch(() => setPixels([]));
    }
  }, [tab]); // eslint-disable-line

  const retry = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/stackadapt/sync/retry/${id}`);
      toast.success("Retry succeeded");
      const r = await api.get("/stackadapt/sync/log"); setLog(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setRetrying(null); }
  };

  const createPixel = async () => {
    if (!pixelForm.name || !pixelForm.advertiser_id) { toast.error("Name and advertiser required"); return; }
    setCreatingPixel(true);
    try {
      await api.post("/stackadapt/pixels", pixelForm);
      toast.success("Conversion pixel created");
      const r = await api.get("/stackadapt/pixels"); setPixels(r.data);
      setPixelForm({ name: "", advertiser_id: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pixel creation failed");
    } finally { setCreatingPixel(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Admin · Execution Engine"
        title="Ad Platform Integration"
        subtitle="Live GraphQL connection — campaign sync, reporting pull, conversion pixels, and error retry queue."
        action={<Btn variant="secondary" onClick={refresh} data-testid="sa-refresh-btn"><RefreshCcw className="w-4 h-4" /> Refresh</Btn>}
      />

      <div className="flex gap-1 border-b border-white/10">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`sa-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-white"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card data-testid="sa-connection-card">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Connection</div>
              {loading ? (
                <div className="mt-3 flex items-center gap-2 text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />
                  <div>
                    <div className="text-sm">Checking live connection…</div>
                    <div className="text-xs text-slate-600">Live GraphQL health call — usually 5–10 seconds</div>
                  </div>
                </div>
              ) : health?.connected ? (
                <div className="mt-3 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-emerald-300" />
                  <div>
                    <div className="font-display text-xl font-bold text-emerald-300">Connected</div>
                    <div className="text-xs text-slate-400">Live · {health.environment}</div>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-2">
                  <XCircle className="w-6 h-6 text-rose-300" />
                  <div>
                    <div className="font-display text-xl font-bold text-rose-300">Not connected</div>
                    <div className="text-xs text-slate-400">{health?.reason || "—"}</div>
                  </div>
                </div>
              )}
            </Card>
            <Card>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Advertisers visible</div>
              <div className="mt-2 font-display text-3xl font-bold">{loading ? "…" : health?.advertisers_visible ?? "—"}</div>
              <div className="text-xs text-slate-500 mt-1">via current API token</div>
            </Card>
            <Card data-testid="sa-mappings-card">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Campaign mappings</div>
              <div className="mt-2 font-display text-3xl font-bold">{loading ? "…" : mappings.length}</div>
              <div className="text-xs text-slate-500 mt-1">TotalReach ⇄ ad platform synced campaigns</div>
            </Card>
          </div>

          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10">
              <h3 className="font-display text-lg font-bold">Advertisers under this account</h3>
            </div>
            {loading ? <Spinner label="Fetching advertisers…" /> : (
              <table className="w-full text-sm" data-testid="advertisers-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="px-5 py-3">Platform ID</th>
                    <th className="px-5 py-3">Advertiser name</th>
                  </tr>
                </thead>
                <tbody>
                  {advs.map(a => (
                    <tr key={a.id} className="border-b border-white/5">
                      <td className="px-5 py-2 font-mono text-xs text-slate-400">{a.id}</td>
                      <td className="px-5 py-2 text-white">{a.name}</td>
                    </tr>
                  ))}
                  {advs.length === 0 && <tr><td colSpan="2" className="px-5 py-6 text-center text-sm text-slate-500">No advertisers visible.</td></tr>}
                </tbody>
              </table>
            )}
          </Card>

          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10">
              <h3 className="font-display text-lg font-bold">Campaign mappings</h3>
            </div>
            <table className="w-full text-sm" data-testid="mappings-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="px-5 py-3">TotalReach campaign</th>
                  <th className="px-5 py-3">SA ID</th>
                  <th className="px-5 py-3">Channel</th>
                  <th className="px-5 py-3">SA status</th>
                  <th className="px-5 py-3">Last synced</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m.id} className="border-b border-white/5">
                    <td className="px-5 py-2 text-white">{m.local_campaign_name}</td>
                    <td className="px-5 py-2 font-mono text-xs text-cyan-300">{m.sa_campaign_id}</td>
                    <td className="px-5 py-2 uppercase text-xs text-slate-300">{m.channel}</td>
                    <td className="px-5 py-2 text-xs text-slate-300">{m.sa_status || "—"}{m.is_draft && " · draft"}</td>
                    <td className="px-5 py-2 text-xs text-slate-500">{(m.last_synced_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
                {mappings.length === 0 && !loading && <tr><td colSpan="5" className="px-5 py-6 text-center text-sm text-slate-500">No campaigns synced yet. Push one from the Campaigns page.</td></tr>}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === "campaigns" && (
        <Card padded={false}>
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold">Live campaigns on the ad platform</h3>
            <span className="text-xs text-slate-500">{saCampaigns ? `${saCampaigns.total} total` : ""}</span>
          </div>
          {saCampaigns === null ? <Spinner label="Fetching live campaigns…" /> : (
            <table className="w-full text-sm" data-testid="sa-campaigns-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="px-5 py-3">SA ID</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Channel</th>
                  <th className="px-5 py-3">Advertiser</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {(saCampaigns.nodes || []).map(c => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="px-5 py-2 font-mono text-xs text-slate-400">{c.id}</td>
                    <td className="px-5 py-2 text-white">{c.name}</td>
                    <td className="px-5 py-2 text-xs text-slate-300">{c.channelType}</td>
                    <td className="px-5 py-2 text-xs text-slate-300">{c.advertiser?.name}</td>
                    <td className="px-5 py-2 text-xs">
                      <Pill status={c.isDraft ? "Draft" : "Active"}>{(c.campaignStatus?.status || "").replace("CAMPAIGN_", "").replace(/_/g, " ").toLowerCase()}</Pill>
                    </td>
                  </tr>
                ))}
                {(saCampaigns.nodes || []).length === 0 && <tr><td colSpan="5" className="px-5 py-6 text-center text-sm text-slate-500">No campaigns visible under this token.</td></tr>}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "synclog" && (
        <Card padded={false}>
          <div className="px-5 py-3 border-b border-white/10">
            <h3 className="font-display text-lg font-bold">Sync log & retry queue</h3>
          </div>
          {log === null ? <Spinner label="Loading sync log…" /> : (
            <table className="w-full text-sm" data-testid="sync-log-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="px-5 py-3">When</th>
                  <th className="px-5 py-3">Action</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Detail</th>
                  <th className="px-5 py-3 text-right">Retry</th>
                </tr>
              </thead>
              <tbody>
                {log.map(l => (
                  <tr key={l.id} className="border-b border-white/5">
                    <td className="px-5 py-2 text-xs text-slate-500 whitespace-nowrap">{(l.created_at || "").slice(5, 16).replace("T", " ")}</td>
                    <td className="px-5 py-2 text-xs text-slate-300">{l.action}</td>
                    <td className="px-5 py-2"><Pill status={l.status === "success" ? "Active" : "Rejected"}>{l.status}</Pill></td>
                    <td className="px-5 py-2 text-xs text-slate-400 max-w-md">
                      {l.status === "failed" ? (
                        <div>
                          <div className="text-rose-300">{l.error_friendly}</div>
                          <div className="text-slate-600 truncate">{l.error_raw}</div>
                        </div>
                      ) : (
                        <span className="text-emerald-300/70">SA #{l.sa_campaign_id || "—"}</span>
                      )}
                    </td>
                    <td className="px-5 py-2 text-right">
                      {l.can_retry && (
                        <Btn variant="secondary" onClick={() => retry(l.id)} disabled={retrying === l.id} data-testid={`retry-${l.id}`}>
                          {retrying === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Retry
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
                {log.length === 0 && <tr><td colSpan="5" className="px-5 py-6 text-center text-sm text-slate-500">No sync activity yet.</td></tr>}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === "pixels" && (
        <>
          <Card>
            <h3 className="font-display text-lg font-bold mb-3">Create conversion pixel (fundraising attribution)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <Input label="Pixel name" value={pixelForm.name} onChange={e => setPixelForm({ ...pixelForm, name: e.target.value })} placeholder="Voss for Senate — Donation pixel" data-testid="pixel-name-input" />
              <Select label="Ad account" value={pixelForm.advertiser_id} onChange={e => setPixelForm({ ...pixelForm, advertiser_id: e.target.value })} data-testid="pixel-advertiser-select">
                <option value="">Select advertiser…</option>
                {advs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
              <Btn onClick={createPixel} disabled={creatingPixel} data-testid="create-pixel-btn">
                {creatingPixel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />} Create pixel
              </Btn>
            </div>
          </Card>
          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10">
              <h3 className="font-display text-lg font-bold">Pixels</h3>
            </div>
            {pixels === null ? <Spinner label="Loading pixels…" /> : (
              <table className="w-full text-sm" data-testid="pixels-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">SA pixel ID</th>
                    <th className="px-5 py-3">Unique key</th>
                    <th className="px-5 py-3">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {pixels.map(p => (
                    <tr key={p.id} className="border-b border-white/5">
                      <td className="px-5 py-2 text-white">{p.name}</td>
                      <td className="px-5 py-2 font-mono text-xs text-cyan-300">{p.sa_pixel_id}</td>
                      <td className="px-5 py-2 font-mono text-xs text-slate-400">{p.unique_key}</td>
                      <td className="px-5 py-2 text-xs text-slate-400">{p.purpose}</td>
                    </tr>
                  ))}
                  {pixels.length === 0 && <tr><td colSpan="4" className="px-5 py-6 text-center text-sm text-slate-500">No pixels yet. Create one above for donation attribution.</td></tr>}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
